/* eslint-disable @typescript-eslint/no-explicit-any */
import { FiClock, FiBarChart2 } from 'react-icons/fi';
import ModernCard from './ModernCard';
import {
  getSiblingEffectiveState,
  QuoteRequest,
} from '../../../../utils/quoteFlowRequests';
import { Job } from '../../../../utils/type';
import ProviderChip from '../../quote-flow/ProviderChip';
import ProwlLine from '../../quote-flow/ProwlLine';
import CommonConfirmModal from './CommonConfirmModal';

// ── S7 — Quote Tracking Dashboard ────────────────────────────────────────

const QuoteTrackingPanel = ({
  request,
  onViewJob,
  onCancelAll,
  isCancelling,
}: {
  request: QuoteRequest;
  onViewJob: (job: Job) => void;
  onCancelAll: (reason: string) => void;
  isCancelling: boolean;
}) => {
  const quotesIn = request.jobs.filter(
    (j) => getSiblingEffectiveState(j) === 'quoted-live'
  );
  // Providers who declined/cancelled will never send a quote, so exclude them
  // from the denominator — it keeps the progress bar honest and lets it
  // reach 100% once everyone who *could* quote has quoted.
  const eligibleProviders = request.jobs.filter((j) => {
    const s = getSiblingEffectiveState(j);
    return s !== 'declined' && s !== 'cancelled';
  });
  const totalProviders = eligibleProviders.length || request.jobs.length;

  return (
    <div className="space-y-4">
      {/* Dynamic Progress Indicator */}
      <ModernCard
        title={
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold">
              <FiClock size={18} />
            </div>
            <div>
              <span className="font-semibold text-sm text-slate-800">
                Quote Dispatch Tracker
              </span>
              <p className="text-xs text-slate-400">
                Broadcasting request to matched local providers
              </p>
            </div>
          </div>
        }
      >
        <div className="space-y-4">
          {/* Visual Progress Bar */}
          <div>
            <div className="flex justify-between text-xs font-bold text-slate-600 mb-2">
              <span>Responses Received</span>
              <span>
                {quotesIn.length} of {totalProviders} Providers Replied
              </span>
            </div>
            <div className="w-full h-3 rounded-full bg-slate-100 overflow-hidden p-0.5 border border-slate-200">
              <div
                className="h-full bg-gradient-to-r from-primary to-cyan-400 rounded-full transition-all duration-500"
                style={{
                  width: `${Math.max(2, (quotesIn.length / totalProviders) * 100)}%`,
                }}
              />
            </div>
          </div>

          {/* Provider Status Chips List */}
          <div className="space-y-2">
            {request.jobs.map((j) => {
              const state = getSiblingEffectiveState(j);
              const isQuoted =
                state === 'quoted-live' || state === 'quoted-expired';
              const itemised = (j.lineItems?.length ?? 0) > 0;
              const serviceTitle =
                j.serviceTitle || j.serviceName || j.service?.title;

              return (
                <div
                  key={j.jobId}
                  className="transition-all hover:translate-x-0.5"
                >
                  <ProviderChip
                    variant="detail-list"
                    name={
                      j.businessName ||
                      j.providerName ||
                      j.provider?.name ||
                      'Provider'
                    }
                    serviceTitle={serviceTitle}
                    state={
                      isQuoted
                        ? 'quoted'
                        : state === 'declined' || state === 'cancelled'
                          ? 'declined'
                          : 'not-yet'
                    }
                    price={j.total ?? 0}
                    meta={
                      isQuoted
                        ? itemised
                          ? 'Itemised quote'
                          : 'Flat price'
                        : undefined
                    }
                    onClick={isQuoted ? () => onViewJob(j) : undefined}
                  />
                </div>
              );
            })}
          </div>

          {quotesIn.length === 0 && (
            <div className="bg-primary/5 rounded-xl p-3 border border-primary/10">
              <ProwlLine text="First reply usually arrives within a few hours. We will notify you instantly." />
            </div>
          )}

          {quotesIn.length >= 1 && quotesIn.length < totalProviders && (
            <div className="flex items-center gap-2.5 bg-primary/5 rounded-xl p-3 border border-primary/10 text-primary">
              <FiBarChart2 size={16} className="shrink-0" />
              <p className="text-xs font-semibold">
                {quotesIn.length} quotes are already in — use "Compare Quotes
                Received" in Request Actions if you don't want to wait for the
                rest.
              </p>
            </div>
          )}
        </div>
      </ModernCard>

      {/* Cancel Request Action */}
      <ModernCard
        title={
          <span className="font-semibold text-sm text-slate-800">
            Request Actions
          </span>
        }
      >
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p className="text-xs font-semibold text-slate-700">
              Need to make changes or cancel?
            </p>
            <p className="text-xs text-slate-400">
              You can cancel this request anytime before accepting a quote.
            </p>
          </div>
          <CommonConfirmModal
            buttonLabel="Cancel Entire Request"
            buttonColor="danger"
            buttonVariant="flat"
            title="Cancel this broadcast request?"
            description="Cancelling closes this request with every provider asked. This action cannot be undone."
            confirmBtnText="Cancel Request"
            confirmBtnColor="danger"
            cancelBtnText="Keep Request"
            requireReason
            reasonRequired
            reasonLabel="Cancellation Reason"
            reasonDescription="Please provide a reason for cancelling this request."
            isLoading={isCancelling}
            onConfirm={(reason) => onCancelAll(reason ?? '')}
            triggerClassName="w-full sm:w-auto"
          />
        </div>
      </ModernCard>
    </div>
  );
};

export default QuoteTrackingPanel;
